import { useState } from "react";
import c1_state from "../model/C1_State";
import { useModel } from "../model/C1_useModel";

function meterLabel(value: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

type SelDim = { axisId: string; nextId: string };

export function C1_DimensionLayer() {
  useModel(c1_state);
  const [sel, setSel] = useState<SelDim | null>(null);
  const [inputVal, setInputVal] = useState("");
  const axes = c1_state.axes;
  const view = c1_state.view;
  const bounds = c1_state.currentBounds;
  const PX = c1_state.svgPx;
  const planScale = PX / 70;
  const numberLocale = state.settings.uiLanguage;
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  const pdfFontSize = renderingPdf ? 11 * planScale : undefined;
  const pdfLineProps = renderingPdf ? { stroke: "#000", strokeWidth: 1.5 * planScale } : {};
  const horizontalTextOffset = (renderingPdf ? 3 : 6) * planScale;
  const verticalTextOffset = (renderingPdf ? 3 : 6) * planScale;

  const verticalAxes = axes.filter((axis) => axis.kind === "v").sort((a, b) => a.pos - b.pos);
  const horizontalAxes = axes.filter((axis) => axis.kind === "h").sort((a, b) => a.pos - b.pos);

  const topY = (bounds.y1 - view.y1 - 1.05) * PX;
  const leftX = (bounds.x1 - view.x1 - 1.05) * PX;
  const overallOffset = 0.5 * PX;
  const topYOverall = topY - overallOffset;
  const leftXOverall = leftX - overallOffset;
  const tick = 7 * planScale;

  function selectDim(axisId: string, nextId: string, currentSpan: number) {
    setSel({ axisId, nextId });
    setInputVal(currentSpan.toFixed(2));
  }

  function commit(axisId: string, nextId: string) {
    const parsed = parseFloat(inputVal.replace(",", "."));
    if (!isNaN(parsed) && parsed > 0.05) {
      const anchor = axes.find((a) => a.id === axisId);
      const movedAxis = axes.find((axis) => axis.id === nextId);
      if (anchor && movedAxis) {
        const delta = anchor.pos + parsed - movedAxis.pos;
        const originalPosition = movedAxis.pos;
        c1_state.transaction(() => {
          axes.forEach((axis) => {
            if (axis.kind === movedAxis.kind && (axis.id === nextId || axis.pos > originalPosition)) {
              axis.setPosition(Math.round((axis.pos + delta) * 100) / 100);
            }
          });
        });
        state.undoManager.userInteractionEnded();
      }
    }
    setSel(null);
  }

  function isSelected(axisId: string, nextId: string) {
    return sel?.axisId === axisId && sel?.nextId === nextId;
  }

  /** Renders either a clickable text label or an inline input */
  function dimLabel(axisId: string, nextId: string, span: number, x: number, y: number, rotate?: string) {
    const selected = isSelected(axisId, nextId);
    const inputW = Math.max(72, Math.min(110, span * PX * 0.8));
    const inputH = 28;

    if (selected) {
      return (
        <foreignObject x={x - inputW / 2} y={y - inputH / 2} width={inputW} height={inputH} overflow="visible">
          <input
            // @ts-expect-error xmlns needed for SVG foreignObject
            xmlns="http://www.w3.org/1999/xhtml"
            type="number"
            min="0.1"
            step="0.1"
            value={inputVal}
            className="C1_dimensionInput"
            autoFocus
            onChange={(e) => setInputVal(e.target.value)}
            onBlur={() => commit(axisId, nextId)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit(axisId, nextId);
              if (e.key === "Escape") setSel(null);
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </foreignObject>
      );
    }

    return (
      <>
        <rect
          x={x - 24}
          y={y - 10}
          width={48}
          height={16}
          fill="transparent"
          className="C1_dimHit"
          style={{ cursor: "default" }}
          transform={rotate}
          onClick={(e) => {
            e.stopPropagation();
            selectDim(axisId, nextId, span);
          }}
        />
        <text
          x={x}
          y={y}
          className="C1_dimensionText"
          textAnchor="middle"
          dominantBaseline="auto"
          transform={rotate}
          style={{ cursor: "default", fontSize: pdfFontSize }}
          onClick={(e) => {
            e.stopPropagation();
            selectDim(axisId, nextId, span);
          }}
        >
          {meterLabel(span, numberLocale)} m
        </text>
      </>
    );
  }

  return (
    <g
      className="C1_dimensionLayer"
      data-c1-svg-layer="dimensions"
      onDoubleClick={(event) => event.stopPropagation()}
      style={{ "--c1-plan-scale": PX / 70 } as React.CSSProperties}
      onClick={() => setSel(null)}
    >
      {verticalAxes.slice(0, -1).map((axis, index) => {
        const next = verticalAxes[index + 1];
        const x1 = (axis.pos - view.x1) * PX;
        const x2 = (next.pos - view.x1) * PX;
        const mid = (x1 + x2) / 2;
        const span = next.pos - axis.pos;

        return (
          <g key={`dim-v-${axis.id}-${next.id}`} className="C1_dimensionGroup">
            <line {...pdfLineProps} x1={x1} y1={topY} x2={x2} y2={topY} className="C1_dimensionLine" />
            <line {...pdfLineProps} x1={x1} y1={topY - tick} x2={x1} y2={topY + tick} className="C1_dimensionLine" />
            <line {...pdfLineProps} x1={x2} y1={topY - tick} x2={x2} y2={topY + tick} className="C1_dimensionLine" />
            {dimLabel(axis.id, next.id, span, mid, topY - horizontalTextOffset)}
          </g>
        );
      })}

      {verticalAxes.length > 1 &&
        (() => {
          const first = verticalAxes[0];
          const last = verticalAxes[verticalAxes.length - 1];
          const x1 = (first.pos - view.x1) * PX;
          const x2 = (last.pos - view.x1) * PX;
          const mid = (x1 + x2) / 2;

          return (
            <g key={`dim-v-overall-${first.id}-${last.id}`} className="C1_dimensionGroup">
              <line {...pdfLineProps} x1={x1} y1={topYOverall} x2={x2} y2={topYOverall} className="C1_dimensionLine" />
              <line
                {...pdfLineProps}
                x1={x1}
                y1={topYOverall - tick}
                x2={x1}
                y2={topYOverall + tick}
                className="C1_dimensionLine"
              />
              <line
                {...pdfLineProps}
                x1={x2}
                y1={topYOverall - tick}
                x2={x2}
                y2={topYOverall + tick}
                className="C1_dimensionLine"
              />
              <text
                x={mid}
                y={topYOverall - horizontalTextOffset}
                className="C1_dimensionText"
                textAnchor="middle"
                style={{ fontSize: pdfFontSize }}
              >
                {meterLabel(last.pos - first.pos, numberLocale)} m
              </text>
            </g>
          );
        })()}

      {horizontalAxes.slice(0, -1).map((axis, index) => {
        const next = horizontalAxes[index + 1];
        const y1 = (axis.pos - view.y1) * PX;
        const y2 = (next.pos - view.y1) * PX;
        const mid = (y1 + y2) / 2;
        const span = next.pos - axis.pos;
        const tx = leftX - verticalTextOffset;

        return (
          <g key={`dim-h-${axis.id}-${next.id}`} className="C1_dimensionGroup">
            <line {...pdfLineProps} x1={leftX} y1={y1} x2={leftX} y2={y2} className="C1_dimensionLine" />
            <line {...pdfLineProps} x1={leftX - tick} y1={y1} x2={leftX + tick} y2={y1} className="C1_dimensionLine" />
            <line {...pdfLineProps} x1={leftX - tick} y1={y2} x2={leftX + tick} y2={y2} className="C1_dimensionLine" />
            {dimLabel(axis.id, next.id, span, tx, mid, `rotate(-90 ${tx} ${mid})`)}
          </g>
        );
      })}

      {horizontalAxes.length > 1 &&
        (() => {
          const first = horizontalAxes[0];
          const last = horizontalAxes[horizontalAxes.length - 1];
          const y1 = (first.pos - view.y1) * PX;
          const y2 = (last.pos - view.y1) * PX;
          const mid = (y1 + y2) / 2;
          const textX = leftXOverall - verticalTextOffset;

          return (
            <g key={`dim-h-overall-${first.id}-${last.id}`} className="C1_dimensionGroup">
              <line
                {...pdfLineProps}
                x1={leftXOverall}
                y1={y1}
                x2={leftXOverall}
                y2={y2}
                className="C1_dimensionLine"
              />
              <line
                {...pdfLineProps}
                x1={leftXOverall - tick}
                y1={y1}
                x2={leftXOverall + tick}
                y2={y1}
                className="C1_dimensionLine"
              />
              <line
                {...pdfLineProps}
                x1={leftXOverall - tick}
                y1={y2}
                x2={leftXOverall + tick}
                y2={y2}
                className="C1_dimensionLine"
              />
              <text
                x={textX}
                y={mid}
                className="C1_dimensionText"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(-90 ${textX} ${mid})`}
                style={{ fontSize: pdfFontSize }}
              >
                {meterLabel(last.pos - first.pos, numberLocale)} m
              </text>
            </g>
          );
        })()}
    </g>
  );
}
